// external libraries
import { useSessionContext } from "@supabase/auth-helpers-react";
import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { createBrowserRouter, createRoutesFromChildren, type RouteObject } from "react-router";
import { RouterProvider } from "react-router/dom";

// utils and constants

import { resolveSessionClearedAction } from "@/app/handleSessionCleared";
import { resolveRoutableUserIdAction } from "@/app/resolveRoutableUserId";
import store from "@/app/store";
import { endReview } from "@features/review-mode/state/reviewSessionSlice";
import { runWhenRecorderIdle, selectRecorderActive } from "@common/lib/recorderActivity";
import { resetRecorderFlags } from "@/state/redux/globalState";
import { useAppSelector } from "@/common/hooks/useRedux";
import { useSessionExpiredRecordingToast } from "@/common/hooks/useSessionExpiredRecordingToast";
import { useSuppressPasswordManagers } from "@/common/hooks/useSuppressPasswordManagers";

// common components
import Spinner from "@/common/components/Spinner";
import AppBanner from "@/common/components/AppBanner";
import ImpersonationBanner from "@/common/components/ImpersonationBanner";
import UnpaidInvoiceBanner from "@/common/components/UnpaidInvoiceBanner";
import RouteErrorFallback from "@/common/components/RouteErrorFallback";

// routes
import { UnAuthenticatedRoutes } from "../routes/UnAuthenticatedRoutes";
import AuthenticatedRoutes from "../routes/AuthenticatedRoutes";
import { RootLayout } from "../routes/RootLayout";
import { getStoredLockState } from "@/features/lock-screen/lockStorage";

function withRouteErrorFallback(routes: RouteObject[]): RouteObject[] {
    return routes.map((route): RouteObject => {
        const errorElement = route.errorElement ?? <RouteErrorFallback />;

        if ("children" in route && route.children) {
            return {
                ...route,
                errorElement,
                children: withRouteErrorFallback(route.children),
            };
        }

        return {
            ...route,
            errorElement,
        };
    });
}

function App() {
    const { session, supabaseClient, isLoading } = useSessionContext();
    const [isIOS, setIsIOS] = useState(false);

    // Key on a stable auth discriminator, NOT the `session` object. Supabase mints a fresh
    // `session` (and `session.user`) reference on every TOKEN_REFRESHED (~hourly) and USER_UPDATED,
    // even when the authenticated user is unchanged. Keying on the object rebuilt the router each
    // refresh for no reason (wasted re-render + a leaked router instance). The route *structure*
    // only depends on authed-vs-not + which user, so we key on the user id (null when logged out).
    // NOTE: use `user?.id`, not `user` — `user` is also a fresh object each refresh. (EN-782)
    const sessionUserId = session?.user?.id ?? null;

    // EN-837: the router below is keyed on the auth discriminator — session-null swaps it to the
    // UnAuthenticatedRoutes tree, unmounting AssetLibraryProvider's MediaRecorder (it lives inside
    // PermissionWrapper, below RouterProvider). Deferring the onAuthStateChange navigate() alone does
    // NOT prevent that — this router rebuild is a separate reactive teardown keyed on the auth
    // discriminator. So we route on a GATED id that holds the last authenticated user while a
    // recording/upload is in flight, releasing the swap to unauthenticated only once the recorder is
    // idle (recording stopped + persisted).
    //
    // Sign-OUT (the transition to null) is the ONLY gated direction. Sign-IN and a genuine user
    // SWITCH are both adopted SYNCHRONOUSLY during render (below) — see resolveRoutableUserIdAction
    // for the full rationale. In short: Supabase's session context starts `isLoading` with
    // `session: null`, so a post-effect-only sign-in update would render UnAuthenticatedRoutes for one
    // paint (login flash on "/", NotFound on deep links). Setting state during render is React's
    // sanctioned derive-state-from-props pattern — React re-renders immediately with no intervening
    // browser paint. A user switch is adopted (not deferred) because the Supabase session is ALREADY
    // the new user, so deferring would only upload the prior user's in-flight clip under the new
    // identity (cross-account misfiling); adopting remounts, and the browser recorder's IndexedDB
    // snapshots bound the loss. (Contrast impersonation: header-only, identity unchanged, so ITS
    // teardown is safely deferred — see impersonationService.ts.)
    //
    // The sign-out release runs through the SAME `runWhenRecorderIdle` primitive (and watchdog) as the
    // session-cleared nav below — NOT a bare reactive `recorderActive` selector. If it were purely
    // reactive, a wedged bounded flag (completing / finalizing that never clears) would let the
    // nav's watchdog fire the sign-out after ~2min while the router stayed pinned to the authenticated
    // shell forever, stranding the user on authenticated routes against a null session. Sharing the
    // primitive means both release on one schedule. A genuinely live capture holds indefinitely (the
    // watchdog escalates to Sentry) rather than tearing down.
    const [routableUserId, setRoutableUserId] = useState<string | null>(sessionUserId);
    const cancelRouterSwapRef = useRef<(() => void) | null>(null);
    if (resolveRoutableUserIdAction(sessionUserId, routableUserId).type === "adoptSync") {
        // Adopt in render for BOTH a sign-in from logged-out (null -> id) and a genuine user switch
        // (id_A -> id_B). A switch cannot be safely deferred — the session is already the new user —
        // so we accept the remount rather than upload the prior user's clip under the new identity.
        // Only a same-identity sign-OUT (-> null) is deferred (in the effect below). The adoptSync
        // guard is self-limiting: once adopted, sessionUserId === routableUserId (-> `none`).
        setRoutableUserId(sessionUserId);
    }
    useEffect(() => {
        const action = resolveRoutableUserIdAction(sessionUserId, routableUserId);
        if (action.type !== "deferSwap") {
            // Steady state, a sign-in/switch already adopted synchronously above, or re-auth to the
            // same user: cancel any swap still pending from a prior recording so a stale teardown
            // can't unmount the shell after the session settled.
            cancelRouterSwapRef.current?.();
            cancelRouterSwapRef.current = null;
            return;
        }
        // Sign-out (-> null): swap the router to the unauthenticated tree only once the recorder is
        // idle. Runs synchronously when already idle (releasing immediately); holds while a
        // recording/upload is in flight. Supersede any prior pending swap first.
        //
        // This is ONE of two idle-gated releases a session-clear schedules — the other is the
        // session-cleared nav in the onAuthStateChange effect below. They are complementary, not
        // redundant: this one swaps the route TREE (routableUserId -> null), that one sets the URL
        // (navigateReplace "/" / hardNav). Their release order is unspecified, but both converge on
        // the unauthenticated tree at "/" regardless of order (navigate is {replace} → no extra
        // history entry; the tree rebuild reads the URL that nav set), so the end state is
        // order-independent. On a deep link the nav is load-bearing (without it the rebuilt
        // unauthenticated tree would land on NotFound).
        const nextRoutableUserId = action.id;
        cancelRouterSwapRef.current?.();
        cancelRouterSwapRef.current = runWhenRecorderIdle(() => {
            cancelRouterSwapRef.current = null;
            // Stale recorder flags are cleared by the identity-teardown effect below (this swap to
            // null trips it), so they can't persist into the next authenticated session on this tab.
            setRoutableUserId(nextRoutableUserId);
        });
        return () => {
            cancelRouterSwapRef.current?.();
            cancelRouterSwapRef.current = null;
        };
    }, [sessionUserId, routableUserId]);

    // EN-837: whenever the router's identity key changes AWAY FROM a real prior user — a sign-out
    // (id -> null) or a cross-tab / shared-device user SWITCH (id_A -> id_B) — the authenticated
    // recorder shell (AssetLibraryProvider's MediaRecorder, inside PermissionWrapper) remounts.
    // useAudioRecorder does NOT clear its Redux busy-flags on unmount, and the store survives a soft
    // (no-reload) swap, so without this the next identity's freshly-mounted shell would inherit the
    // prior user's stale completing / recording / uploading flags (e.g. a watchdog-released-but-still-
    // true hold) — wrongly showing an in-progress recording or disabling Sign Out, recoverable only
    // by a manual reload. Reset ONLY when leaving a non-null prior identity: never on the initial
    // mount or a clean sign-in (null -> id), so an in-progress recording restored by startup recovery
    // (EN-836) is not clobbered. For the sign-out path this fires at idle (the swap to null is itself
    // gated by runWhenRecorderIdle above); a switch adopts immediately (we've accepted A's remount, so
    // clearing A's now-orphaned flags right away is correct). Hard-nav sign-outs reload → store resets
    // anyway, so this covers the soft-swap + switch paths.
    const prevRoutableUserIdRef = useRef(routableUserId);
    useEffect(() => {
        const prev = prevRoutableUserIdRef.current;
        prevRoutableUserIdRef.current = routableUserId;
        if (prev !== null && prev !== routableUserId) {
            store.dispatch(resetRecorderFlags());
            // EN-18: a review (and its sessionStorage copy) belongs to the user who started it.
            store.dispatch(endReview());
        }
    }, [routableUserId]);

    // EN-837: while the router swap is deferred, the authenticated shell stays mounted and
    // interactive on an already-dead session (routableUserId holds the old user; sessionUserId is
    // null). The recorder is protected, but any OTHER action the user takes in that window silently
    // 401s. Surface a persistent notice so the effectively-signed-out state isn't silent. Gated on an
    // active recorder so it never flashes during an ordinary idle sign-out (where the swap fires in
    // the same tick and the id-only condition is briefly true).
    const recorderActive = useAppSelector((state) => selectRecorderActive(state.globalState));
    useSessionExpiredRecordingToast(sessionUserId === null && routableUserId !== null && recorderActive);

    // Suppress third-party password managers (1Password/LastPass/Bitwarden/Dashlane/Proton Pass)
    // and browser autofill on ordinary text fields, but ONLY in the authenticated tree — gate on
    // the same `routableUserId !== null` discriminator the router below keys on, so login / signup
    // / reset-password (the UnAuthenticatedRoutes tree) are untouched and managers work there.
    // `type="password"` fields (UpdatePassword, PIN entry) are skipped regardless. (see
    // useSuppressPasswordManagers / suppressPasswordManagers)
    useSuppressPasswordManagers(routableUserId !== null);

    // Keyed on routableUserId, so it rebuilds exactly ONCE per committed value. The render-phase
    // setRoutableUserId in the adoptSync branch above does NOT double-construct the router: useMemo
    // compares deps against the last COMMITTED render, and in the discarded pass that setState
    // triggers, routableUserId still holds the committed value — so this dep is unchanged → cache
    // hit, createBrowserRouter is not re-run in the throwaway pass (no orphaned history/popstate
    // listener from it). Construction count is identical to the prior sessionUserId keying (EN-782).
    const router = useMemo(() => {
        const routes = createRoutesFromChildren(routableUserId ? AuthenticatedRoutes() : UnAuthenticatedRoutes());
        // Pathless root layout mounts TitleManager (needs router context for useMatches)
        // as the single route-wide injection point for both route trees.
        const wrapped: RouteObject[] = [
            {
                element: <RootLayout />,
                errorElement: <RouteErrorFallback />,
                children: withRouteErrorFallback(routes),
            },
        ];
        return createBrowserRouter(wrapped);
    }, [routableUserId]);

    const routerRef = useRef(router);
    routerRef.current = router;

    // Cancel handle for a session-cleared navigation we deferred (during an active recording) until
    // the recorder goes idle. Captured so a re-authentication can abort it before the stale nav runs.
    const cancelDeferredNavRef = useRef<(() => void) | null>(null);

    useEffect(() => {
        const { data: authListener } = supabaseClient.auth.onAuthStateChange((event, session) => {
            if (session) {
                // A valid session exists again (sign-in, cross-tab SIGNED_IN, or token refresh). If a
                // session-cleared nav was deferred during an active recording and the session then
                // became valid again, that pending nav (a hardNav to /resend-verification-email, or a
                // navigateReplace to "/") would still fire once the recorder goes idle and yank the
                // user off the now-valid session. Cancel it. No-op when nothing is pending. (EN-837)
                cancelDeferredNavRef.current?.();
                cancelDeferredNavRef.current = null;
            }
            if (!session && event !== "INITIAL_SESSION") {
                // Check URL query params for OTP expiration or invalid email link errors
                let urlParams = new URLSearchParams(window.location.search);
                // Supabase auth redirects with query params after the hash for some reason
                if (!urlParams.get("error_code") && window.location.hash && window.location.hash.startsWith("#error")) {
                    urlParams = new URLSearchParams(window.location.hash.substring(1));
                }
                const errorCode = urlParams.get("error_code");
                const errorDescription = urlParams.get("error_description");

                const resolveAction = () =>
                    resolveSessionClearedAction({
                        pathname: window.location.pathname,
                        errorCode,
                        errorDescription,
                        isLocked: getStoredLockState().isLocked,
                    });

                // Early out so we don't schedule a deferred no-op.
                if (resolveAction().type === "noop") return;

                // Supersede any nav still pending from a previous session-clear so we don't leak a
                // store subscription or run a stale action.
                cancelDeferredNavRef.current?.();

                // Defer the nav/hard-nav until the recorder is idle so we don't tear down an
                // in-flight recording/upload (runs synchronously when already idle). Re-resolve at
                // execution time: device-lock state can change during the deferred window, and a lock
                // engaged mid-recording must suppress the (now stale) navigation —
                // resolveSessionClearedAction returns noop when locked. Capture the cancel handle so a
                // re-auth (above) can abort the pending nav before it fires.
                cancelDeferredNavRef.current = runWhenRecorderIdle(() => {
                    cancelDeferredNavRef.current = null;
                    const action = resolveAction();
                    switch (action.type) {
                        case "noop":
                            // Re-resolved to noop during the deferred window (e.g. device locked
                            // mid-recording) — the pending nav is now stale; do nothing.
                            break;
                        case "hardNav":
                            window.location.href = action.href;
                            break;
                        case "navigateReplace":
                            void routerRef.current.navigate(action.to, { replace: true });
                            break;
                        default: {
                            // Exhaustiveness: a new SessionClearedAction variant must be handled here.
                            const _exhaustive: never = action;
                            void _exhaustive;
                        }
                    }
                });
            }
        });

        const userAgent = window.navigator.userAgent.toLowerCase();
        const isIOSDevice = /iphone|ipad|ipod/.test(userAgent);
        setIsIOS(isIOSDevice);

        return () => {
            authListener.subscription.unsubscribe();
            // Cancel any session-cleared nav still deferred by runWhenRecorderIdle. Without this, on a
            // supabaseClient change or App unmount the pending callback would fire later against a
            // stale `routerRef.current` (or hardNav) after this listener is gone — mirroring the
            // cancel-on-cleanup the routableUserId effect above already does for its swap. (EN-837)
            cancelDeferredNavRef.current?.();
            cancelDeferredNavRef.current = null;
        };
    }, [supabaseClient]);

    if (isLoading) {
        return (
            <div className="w-full h-full flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    return (
        <Suspense
            fallback={
                <div className="w-full h-full flex items-center justify-center">
                    <Spinner />
                </div>
            }
        >
            {isIOS && <AppBanner />}
            <ImpersonationBanner />
            <UnpaidInvoiceBanner />
            {/* EN-853: key on routableUserId (the same value the router useMemo is keyed on) so a
                router rebuild REMOUNTS the provider instead of hot-swapping the live router instance in
                place. React Router does not support swapping a live router; the in-place swap paints a
                stale route (SignUp) at "/" after a mid-session SIGNED_OUT even though the new router
                already matches "/"→Login. Keying on routableUserId (not sessionUserId) keeps the provider
                key aligned with the router it wraps through EN-837's idle-gated sign-out swap; it also
                remounts on a user SWITCH, matching EN-837's "adopting remounts" intent. */}
            <RouterProvider key={routableUserId ?? "anon"} router={router} />
        </Suspense>
    );
}

export default App;
